import { GROUP_TYPE_KEYS } from '@pushly/pquery-core'
/**
 * A calculated field as the domain's calculated-fields endpoint returns it. Only
 * the fields the unsafe check reads are named; the payload carries more.
 *
 * The two casings for the last-calculated timestamp are both accepted because the
 * endpoint has been seen to answer in either, and the check must not turn safe
 * into unsafe on a casing it did not expect.
 */
export interface ReachCalculatedField {
    name: string
    expression: string
    lastCalculatedAt?: string | null
    last_calculated_at?: string | null
}

/**
 * The three properties whose audience cannot be counted until a background pass
 * has computed it. A condition on any of these is unsafe until a calculated field
 * proves the computation has run for that exact expression.
 */
export const DELAYED_PROPERTIES = [
    'behaviors.page_audience',
    'behaviors.page_tag_audience',
    'behaviors.tag_clicked_audience',
] as const

const DELAYED = new Set<string>(DELAYED_PROPERTIES)

interface DelayedConditionValue {
    meta?: { expression?: string }
}

/**
 * Whether a serialized query would target an audience the platform has not yet
 * computed.
 *
 * A condition on a delayed property is unsafe unless a calculated field matches it
 * on name AND expression AND records that the computation has already run. The
 * expression is compared with both sides normalized: a stored field whose
 * expression differs only in case still counts as the same computation, so a
 * genuinely-calculated segment is not held back as unsafe over letter case alone.
 */
export function isUnsafeExecution(
    document: Record<string, unknown> | undefined,
    calculatedFields: readonly ReachCalculatedField[],
): boolean {
    const group = readGroup(document)
    return group ? groupContainsUnsafeCondition(group, calculatedFields) : false
}

function readGroup(node: Record<string, unknown> | undefined): unknown[] | undefined {
    if (!node) return undefined

    // Every group key the wire format defines, from the engine's own set rather than a list kept
    // here. Missing `and_not` made this fail open: the node was not recognised as a group, fell
    // through to the condition branch, and every condition beneath it went unexamined — so a
    // delayed property inside a negated group read as computed when it was not.
    for (const key of GROUP_TYPE_KEYS) {
        const branch = node[key]
        if (Array.isArray(branch)) return branch
    }

    return undefined
}

function groupContainsUnsafeCondition(
    group: readonly unknown[],
    calculatedFields: readonly ReachCalculatedField[],
): boolean {
    for (const entry of group) {
        if (!entry || typeof entry !== 'object') continue
        const node = entry as Record<string, unknown>

        const subGroup = readGroup(node)
        if (subGroup) {
            if (groupContainsUnsafeCondition(subGroup, calculatedFields)) return true
            continue
        }

        const property = Object.keys(node)[0]
        if (!DELAYED.has(property)) continue
        if (conditionIsUnsafe(property, node[property], calculatedFields)) return true
    }

    return false
}

function conditionIsUnsafe(
    property: string,
    value: unknown,
    calculatedFields: readonly ReachCalculatedField[],
): boolean {
    const expression = normalize((value as DelayedConditionValue)?.meta?.expression)

    for (const field of calculatedFields) {
        if (field.name === property && normalize(field.expression) === expression) {
            // The one calculated field this condition maps to. It is safe only
            // once the background computation has recorded a run for it.
            return !(field.lastCalculatedAt || field.last_calculated_at)
        }
    }

    return true
}

const normalize = (value: string | undefined | null): string => String(value ?? '').toLowerCase()

/**
 * What a reader is told about a segment whose audience has not been computed yet.
 *
 * Two different messages for two different situations, and only one of them is a refusal. Inside a
 * notification's own drawer the segment cannot be created at all, so the text says so. Everywhere
 * else the segment is perfectly savable and it is the size estimate that is unavailable — that text
 * describes a wait, and using it to refuse a save reads as an error where nothing is wrong.
 */
export function getUnsafeExecutionText(context?: { isDrawerMode?: boolean }): string {
    if (context?.isDrawerMode) {
        return (
            'One or more conditions in this segment require additional processing time to build. ' +
            'Because of that, this segment cannot be created from the notification creation page.'
        )
    }

    // Deliberately not phrased as a refusal: the segment saves, and becomes targetable once the
    // computation finishes. Saying only that the audience "will not be available" states the
    // unavailability without the "until then" that makes it true.
    return (
        'One or more conditions in this segment require additional processing time to calculate the ' +
        'size. Until that finishes the size estimate is not shown and the audience is not available ' +
        'for targeting.'
    )
}
